// LLM generated - Model Claude 4 Sonnet
/**
 * Polyfill for React 19's useOptimistic hook.
 * Provides optimistic UI updates with immediate display and smooth server sync.
 *
 * Key behavior:
 * - Optimistic updates are applied IMMEDIATELY (high priority, no transition)
 * - Server sync uses useTransition for smooth, non-blocking updates
 *
 * @see https://react.dev/reference/react/useOptimistic (React 19 docs)
 *
 * When the project upgrades to React 19, this can be replaced with:
 * import { useOptimistic } from 'react';
 */
import { useState, useRef, useCallback } from 'react';

/**
 * A hook that lets you show a different state while an async action is underway.
 *
 * @param value - The current server/actual value
 * @param reducer - A function that takes the current state and an action, returns the new optimistic state
 * @returns A tuple of [optimisticValue, addOptimistic, clearOptimistic, isPending]
 *
 * @example
 * ```tsx
 * const [optimisticMessages, addOptimisticMessage, clearOptimisticMessages] = useOptimistic(
 *   messages,
 *   (state, newMessage) => [newMessage, ...state] // Prepend for chat
 * );
 *
 * async function sendMessage(content: string) {
 *   const optimisticMessage = { id: 'temp', content, pending: true };
 *   addOptimisticMessage(optimisticMessage);
 *   await api.sendMessage(content);
 *   // When Pusher confirms, call clearOptimisticMessages()
 * }
 * ```
 */
export function useOptimistic<T, A = T>(
  value: T,
  reducer: (state: T, action: A) => T
): [T, (action: A) => void, () => void, boolean] {
  // Store pending actions - these persist across renders until cleared
  const pendingActionsRef = useRef<A[]>([]);

  // Counter to force re-renders when optimistic updates are applied
  const [, forceRender] = useState(0);

  // Compute the display value by applying all pending actions to server value
  // This happens on EVERY render, so pending actions are always applied to latest server state
  let displayValue: T = value;
  if (pendingActionsRef.current.length > 0) {
    displayValue = pendingActionsRef.current.reduce((acc, action) => reducer(acc, action), value);
  }

  // Apply optimistic update IMMEDIATELY
  const applyOptimistic = useCallback((action: A) => {
    pendingActionsRef.current = [...pendingActionsRef.current, action];
    // Force re-render to show the optimistic update
    forceRender((c) => c + 1);
  }, []);

  // Clear all pending optimistic updates
  // Call this when server has caught up (e.g., after Pusher confirms)
  const clearOptimistic = useCallback(() => {
    if (pendingActionsRef.current.length > 0) {
      pendingActionsRef.current = [];
      forceRender((c) => c + 1);
    }
  }, []);

  const isPending = pendingActionsRef.current.length > 0;

  return [displayValue, applyOptimistic, clearOptimistic, isPending];
}

export default useOptimistic;
